import axiosInstance, { Axios, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Maker } from './buildMachine';
import { Ctx, Snap } from './core';
import { consoleLogger } from '@shared/logging/consoleLogger';
import { getResourcesOrigin } from '@shared/ui/elements/getResourcesOrigin';
import { getContentPath } from '@shared/content/getContentPath';
import { addMinutes } from 'date-fns/addMinutes';
import { v4 as uuidv4 } from 'uuid';
import { getHost } from '@shared/getUtiqHost';

export const init: Maker<Ctx, Snap, null> = ({
  makeEffect,
  makeEffectGroup,
}) => {
  function attachAxiosInterceptors(axios: Axios) {
    consoleLogger.logDebug('Setting Axios calls interceptors...');
    axios.interceptors.request.use(
      (config) => {
        consoleLogger.logTrace(
          `Calling url ${config.url} with data:`,
          config.data,
        );
        return config;
      },
      (error) => {
        consoleLogger.logError(
          'Error occurred when setting up a request:',
          error.message,
        );
        return Promise.reject(error);
      },
    );

    axios.interceptors.response.use(
      (response) => {
        consoleLogger.logTrace(
          `Response from ${response.config.url} with data:`,
          response.data,
          'and status',
          response.status,
        );
        return response;
      },
      (error) => {
        if (error.response) {
          consoleLogger.logTrace(
            'Call for',
            error.response.config.url,
            'failed with response status',
            error.response.status,
            'and reason:',
            error.response.data,
          );
        } else if (error.request) {
          consoleLogger.logError(
            'No response received for a call: ',
            error.request,
          );
        } else {
          consoleLogger.logError('General error occurred: ', error.message);
        }
        return Promise.reject(error);
      },
    );

    consoleLogger.logDebug('Finished setting Axios calls interceptors.');
  }

  return [
    makeEffect(
      'axiosGlobal',
      (_, { ctx, name, cond }) => cond(!ctx.times[name], '!ctx.times[name]'),
      (_, { ctx, name, snap }) => {
        attachAxiosInterceptors(axiosInstance);
        ctx.times[name] = snap.now;
      },
    ),

    makeEffect(
      'axiosLocal',
      (_, { ctx, name, cond }) => cond(!ctx.times[name], '!ctx.times[name]'),
      (_, { ctx, name, snap, run }) => {
        ctx.times[name] = snap.now;
        const axios = axiosInstance.create();
        attachAxiosInterceptors(axios);

        ctx.axios.instance = (req: AxiosRequestConfig) => {
          const bundle = { req, whenReq: new Date() } as {
            req: AxiosRequestConfig;
            whenReq: Date;
            res?: AxiosResponse;
            whenRes?: Date;
          };
          ctx.axios.active.push(bundle);
          ctx.axios.log.push(bundle);

          axios
            .request({
              ...req,
              transformResponse: (res) => {
                try {
                  return JSON.parse(res);
                } catch {
                  return res;
                }
              },
            })
            .catch((x) => x)
            .then((res) => {
              bundle.res = res;
              bundle.whenRes = new Date();
              run();
            });
        };
      },
    ),

    makeEffect(
      'iframeMsgs',
      (_, { ctx, name, cond }) => cond(!ctx.times[name], '!ctx.times[name]'),
      (_, { ctx, name, snap, run }) => {
        ctx.times[name] = snap.now;
        ctx.iframeMsgs = [];

        window.addEventListener(
          'message',
          (evt) => {
            const msg = (() => {
              try {
                if (evt.data) {
                  try {
                    return JSON.parse(evt.data);
                  } catch {
                    return evt.data;
                  }
                }
              } catch {
                void 0;
              }
              return null;
            })();

            ctx.iframeMsgs?.push({ evt, msg, when: new Date() });
            run();
          },
          false,
        );
      },
    ),

    makeEffectGroup(
      'templateData',
      (_, { ctx, pass, skip }) => {
        if (ctx.window.Utiq?.data?.popupContent) return skip('loaded');
        if (ctx.isIdConnect) return skip('isIdConnect');
        return pass('not loaded');
      },

      ({ makeEffect }) => [
        makeEffect(
          'get',
          (_, { ctx, pass, skip }) => {
            const origin = getResourcesOrigin();
            const { host } = getHost();
            const url =
              origin +
              getContentPath({
                type: 'templates',
                filename: 'templateData.json',
                host,
              });

            if (ctx.axios.active.find(({ req }) => req.url === url))
              return skip('active');

            return pass(url);
          },
          (url, { ctx }) => ctx.axios.instance({ url }),
        ),

        makeEffect(
          '200',
          (_, { ctx, skip, pass }) => {
            const origin = getResourcesOrigin();
            const { host } = getHost();
            const url =
              origin +
              getContentPath({
                type: 'templates',
                filename: 'templateData.json',
                host,
              });

            if (
              ctx.axios.active[0]?.req.url === url &&
              ctx.axios.active[0]?.res?.status === 200
            ) {
              const { data } = ctx.axios.active[0]?.res;
              if (
                typeof data === 'object' &&
                data !== null &&
                Object.keys(data).length > 0
              )
                return pass(data);

              return skip('invalid data response', data);
            }

            return skip('');
          },
          (data, { ctx }) => {
            ctx.axios.active.shift();

            ctx.window.Utiq ||= {};
            ctx.window.Utiq.data ||= {};
            ctx.window.Utiq.data.popupContent = data;
          },
        ),

        makeEffect(
          'notOk',
          (_, { ctx, pass, skip }) => {
            const origin =
              window.location.origin === 'integration.test'
                ? 'https://utiqcontent.com'
                : getResourcesOrigin();

            const { host } = getHost();
            const url =
              origin +
              getContentPath({
                type: 'templates',
                filename: 'templateData.json',
                host,
              });

            if (
              ctx.axios.active[0]?.req.url === url &&
              ctx.axios.active[0]?.res
            )
              return pass(
                !ctx.window.Utiq?.config?.CMP ||
                  ctx.window.Utiq?.config?.CMP === 'none',
              );
            else return skip('');
          },

          (shouldSetEligibility, { ctx, halt, name, snap }) => {
            ctx.axios.active.shift();

            if (shouldSetEligibility)
              ctx.window.localStorage.setItem(
                'utiqEligibility',
                JSON.stringify({
                  isEligible: true,
                  retryEligibilityCheckTime: addMinutes(snap.now, 10).getTime(),
                  eligibilityCheckTime: snap.now.getTime(),
                }),
              );

            ctx.window.Utiq ||= {};
            ctx.window.Utiq.data ||= {};

            halt(name);
          },
        ),
      ],
    ),

    makeEffect(
      /* Create the transactionId that is added to each backend call to track same-pageload calls in logs - in the Utiq namespace */
      'createTransactionId',
      (_, { ctx, pass, skip }) => {
        if (!ctx.window.Utiq?.transactionId) {
          const id = '1337' + uuidv4().slice(4);
          return pass(id);
        } else return skip();
      },
      (newId, { ctx }) => {
        consoleLogger.logDebug('No transaction ID present. Creating');
        ctx.window.Utiq!.transactionId = newId;
        consoleLogger.logDebug(
          `New transactionId: ${ctx.window.Utiq!.transactionId}`,
        );
      },
    ),
  ];
};
